import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"

import { Progress } from "./ui/progress"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
} from "./ui/sidebar"
import {
  Calendar,
  Cloud,
  Droplets,
  Home,
  Leaf,
  Settings,
  Sun,
  User,
  CheckCircle2,
  LogOut,
  Menu,
  X,
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  TrendingUp,
  AlertTriangle,
  Heart,
  Zap,
} from "lucide-react"
import SharedHeader from './SharedHeader'

// Sample user's plants data
const sampleUserPlants = [
  {
    id: 1,
    name: "Rožė",
    scientificName: "Rosa",
    category: "Gėlės",
    addedDate: "2024-01-15",
    lastWatered: "2024-07-23",
    nextWatering: "2024-07-25",
    healthStatus: "excellent",
    growthStage: "flowering",
    location: "Balkonas",
    notes: "Gražiai žydi, reikia dažniau laistyti",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop"
  },
  {
    id: 2,
    name: "Pomidoras",
    scientificName: "Solanum lycopersicum",
    category: "Daržovės",
    addedDate: "2024-03-20",
    lastWatered: "2024-07-24",
    nextWatering: "2024-07-24",
    healthStatus: "good",
    growthStage: "fruiting",
    location: "Sodas",
    notes: "Pradeda raudonėti, reikia tręšti",
    image: "https://images.unsplash.com/photo-1592924357228-91a4daadcfea?w=400&h=300&fit=crop"
  },
  {
    id: 3,
    name: "Ožiarūtis",
    scientificName: "Thymus",
    category: "Žolynai",
    addedDate: "2024-02-10",
    lastWatered: "2024-07-22",
    nextWatering: "2024-07-26",
    healthStatus: "excellent",
    growthStage: "mature",
    location: "Virtuvės langas",
    notes: "Aromatingas, naudojamas arbatai",
    image: "https://images.unsplash.com/photo-1551522435-a13afa10f103?w=400&h=300&fit=crop"
  }
]

export default function MyPlants() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')



  // Show loading state while authentication is being checked
  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700">Kraunama...</p>
        </div>
      </div>
    )
  }

  const navigationItems = [
    {
      title: "Pagrindinis",
      description: "Pagrindinis skydelis",
      url: '/dashboard',
      icon: Home
    },
    {
      title: "Mano Sodas",
      description: "Jūsų augalų sąrašas",
      url: '/my-plants',
      icon: Heart
    },
    {
      title: "Augalų Katalogas",
      description: "Augalų duomenų bazė",
      url: '/plants',
      icon: Leaf
    },
    {
      title: "Užduotys",
      description: "Priežiūros užduotys",
      url: '/tasks',
      icon: CheckCircle2
    },
    {
      title: "Kalendorius",
      description: "Priežiūros kalendorius",
      url: '/calendar',
      icon: Calendar
    },
    {
      title: "Orai",
      description: "Oro prognozės",
      url: '/weather',
      icon: Cloud
    }
  ]



  const handleNavigation = (url: string) => {
    navigate(url)
    setMobileSidebarOpen(false)
  }



  const isActiveRoute = (url: string) => {
    return location.pathname === url
  }

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-emerald-600 bg-emerald-50'
      case 'good':
        return 'text-green-600 bg-green-50'
      case 'fair':
        return 'text-amber-600 bg-amber-50'
      case 'poor':
        return 'text-red-600 bg-red-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  const getHealthStatusText = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'Puikus'
      case 'good':
        return 'Geras'
      case 'fair':
        return 'Vidutinis'
      case 'poor':
        return 'Blogas'
      default:
        return 'Nežinomas'
    }
  }

  const getGrowthStageText = (stage: string) => {
    switch (stage) {
      case 'seedling':
        return 'Sėjūnas'
      case 'vegetative':
        return 'Augimas'
      case 'flowering':
        return 'Žydėjimas'
      case 'fruiting':
        return 'Vaisavimas'
      case 'mature':
        return 'Subrendęs'
      default:
        return stage
    }
  }

  const getDaysUntilWatering = (nextWatering: string) => {
    const today = new Date()
    const nextDate = new Date(nextWatering)
    const diffTime = nextDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getWateringUrgency = (days: number) => {
    if (days <= 0) return 'urgent'
    if (days <= 1) return 'soon'
    if (days <= 3) return 'upcoming'
    return 'normal'
  }

  const getWateringUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent':
        return 'text-red-600 bg-red-50'
      case 'soon':
        return 'text-amber-600 bg-amber-50'
      case 'upcoming':
        return 'text-blue-600 bg-blue-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  // Filter plants based on search and status
  const filteredPlants = sampleUserPlants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plant.scientificName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plant.location.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = selectedStatus === 'all' || plant.healthStatus === selectedStatus

    return matchesSearch && matchesStatus
  })

  const handleAddPlant = () => {
    navigate('/plants')
  }

  const handleEditPlant = (plantId: number) => {
    // TODO: Implement edit plant functionality
    console.log(`Editing plant ${plantId}`)
  }

  const handleDeletePlant = (plantId: number) => {
    // TODO: Implement delete plant functionality
    console.log(`Deleting plant ${plantId}`)
  }

  const handleWaterPlant = (plantId: number) => {
    // TODO: Implement water plant functionality
    console.log(`Watering plant ${plantId}`)
  }

  // Plant Placeholder Component
  const PlantPlaceholder = () => (
    <div className="w-full h-full bg-emerald-50 flex items-center justify-center rounded-lg">
      <Leaf className="w-12 h-12 text-emerald-400" />
    </div>
  )

  return (
    <div className="flex h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 my-plants-page">
      {/* Desktop Sidebar */}
      <Sidebar className={`hidden lg:flex w-64 flex-col bg-white/80 backdrop-blur-sm border-r border-emerald-100 shadow-2xl transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        {/* Sidebar Header */}
        <SidebarHeader className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
              <Leaf className="h-5 w-5" />
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-emerald-800">{t('branding.appName')}</span>
                <span className="text-xs text-emerald-600">{t('dashboard.sidebar.subtitle')}</span>
              </div>
            )}
          </div>
        </SidebarHeader>
        
        {/* Navigation Content */}
        <SidebarContent className="flex-1 bg-white/80 backdrop-blur-sm px-3 py-4">
          <SidebarNav className="space-y-1">
            {navigationItems.map((item) => {
              const isActive = isActiveRoute(item.url)
              return (
                <SidebarNavItem
                  key={item.title}
                  active={isActive}
                  className={`
                    group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                    ${isActive 
                      ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                      : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }
                  `}
                  onClick={() => handleNavigation(item.url)}
                >
                  <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                  {sidebarOpen && (
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                    </div>
                  )}
                </SidebarNavItem>
              )
            })}
          </SidebarNav>
        </SidebarContent>
        
        {/* Sidebar Footer */}
        <SidebarFooter className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
          <SidebarNav>
            <SidebarNavItem 
              className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
            >
              <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
              {sidebarOpen && (
                <div className="flex flex-col">
                  <span>{t('dashboard.navigation.settings')}</span>
                  <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                </div>
              )}
            </SidebarNavItem>
          </SidebarNav>
        </SidebarFooter>
      </Sidebar>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm shadow-2xl">
            <div className="flex h-full flex-col">
              {/* Mobile Header */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                      <Leaf className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-emerald-800">{t('branding.appName')}</span>
                      <span className="text-xs text-emerald-600">{t('dashboard.sidebar.subtitle')}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-emerald-700 hover:bg-emerald-100"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="flex-1 px-3 py-4">
                <SidebarNav className="space-y-1">
                  {navigationItems.map((item) => {
                    const isActive = isActiveRoute(item.url)
                    return (
                      <SidebarNavItem
                        key={item.title}
                        active={isActive}
                        className={`
                          group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                          ${isActive 
                            ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }
                        `}
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                        <div className="flex flex-col">
                          <span>{item.title}</span>
                          <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                        </div>
                      </SidebarNavItem>
                    )
                  })}
                </SidebarNav>
              </div>

              {/* Mobile Footer */}
              <div className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
                <SidebarNav>
                  <SidebarNavItem 
                    href="#" 
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200"
                  >
                    <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                    <div className="flex flex-col">
                      <span>{t('dashboard.navigation.settings')}</span>
                      <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                    </div>
                  </SidebarNavItem>
                </SidebarNav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <SharedHeader 
          title="Mano Sodas"
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileSidebarOpen={mobileSidebarOpen}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-emerald-800 mb-2">Mano Sodas</h2>
                <p className="text-gray-600">Valdykite savo augalų kolekciją ir priežiūrą</p>
              </div>
              <Button
                onClick={handleAddPlant}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Pridėti Augalą
              </Button>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Ieškoti augalų..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">Visi augalai</option>
                <option value="excellent">Puikus</option>
                <option value="good">Geras</option>
                <option value="fair">Vidutinis</option>
                <option value="poor">Blogas</option>
              </select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Iš viso augalų</CardTitle>
                <Heart className="h-4 w-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{filteredPlants.length}</div>
                <p className="text-xs text-gray-500 mt-1">Jūsų kolekcijoje</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Šiandien laistyti</CardTitle>
                <Droplets className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredPlants.filter(p => getDaysUntilWatering(p.nextWatering) <= 0).length}
                </div>
                <p className="text-xs text-gray-500 mt-1">Reikia laistyti</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Puikios būklės</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredPlants.filter(p => p.healthStatus === 'excellent').length}
                </div>
                <p className="text-xs text-gray-500 mt-1">Augalai klesti</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Dėmesio reikalauja</CardTitle>
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredPlants.filter(p => p.healthStatus === 'poor' || p.healthStatus === 'fair').length}
                </div>
                <p className="text-xs text-gray-500 mt-1">Reikia priežiūros</p>
              </CardContent>
            </Card>
          </div>

          {/* Plants Grid */}
          {filteredPlants.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredPlants.map((plant) => {
                const daysUntilWatering = getDaysUntilWatering(plant.nextWatering)
                const wateringUrgency = getWateringUrgency(daysUntilWatering)
                
                return (
                  <Card key={plant.id} className="border-0 shadow-2xl hover:shadow-3xl transition-shadow duration-200 bg-white/80 backdrop-blur-sm overflow-hidden">
                    <div className="aspect-square bg-emerald-50 flex items-center justify-center relative overflow-hidden rounded-t-lg">
                      <img
                        src={plant.image}
                        alt={`${plant.name} (${plant.scientificName})`}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.nextElementSibling?.classList.remove('hidden')
                        }}
                      />
                      <div className="hidden absolute inset-0">
                        <PlantPlaceholder />
                      </div>
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 bg-white/80 hover:bg-white"
                          onClick={() => handleEditPlant(plant.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 bg-white/80 hover:bg-white text-red-600 hover:text-red-700"
                          onClick={() => handleDeletePlant(plant.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                            {plant.name}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-500 italic">
                            {plant.scientificName}
                          </CardDescription>
                        </div>
                        <Badge className={`text-xs ${getHealthStatusColor(plant.healthStatus)}`}>
                          {getHealthStatusText(plant.healthStatus)}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        {/* Location */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="font-medium">Vieta:</span>
                          <span>{plant.location}</span>
                        </div>

                        {/* Growth Stage */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="font-medium">Stadija:</span>
                          <span>{getGrowthStageText(plant.growthStage)}</span>
                        </div>

                        {/* Watering Status */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Droplets className="h-4 w-4" />
                            <span>Laistymas:</span>
                          </div>
                          <Badge className={`text-xs ${getWateringUrgencyColor(wateringUrgency)}`}>
                            {daysUntilWatering <= 0 ? 'Šiandien' : 
                             daysUntilWatering === 1 ? 'Rytoj' : 
                             `${daysUntilWatering} d.`}
                          </Badge>
                        </div>

                        {/* Notes */}
                        {plant.notes && (
                          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                            <span className="font-medium">Pastabos:</span> {plant.notes}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleWaterPlant(plant.id)}
                          disabled={daysUntilWatering > 0}
                        >
                          <Droplets className="h-4 w-4 mr-2" />
                          {daysUntilWatering <= 0 ? 'Laistyti' : 'Jau laistyta'}
                        </Button>
                        <Button
                          variant="outline"
                          className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleEditPlant(plant.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-12">
              <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Neturite augalų</h3>
              <p className="text-gray-500 mb-6">Pradėkite savo sodo kelionę pridėdami pirmąjį augalą</p>
              <Button
                onClick={handleAddPlant}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Pridėti iš Katalogo
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  )
} 